// src/pages/AdminChargeAccount.jsx
import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Wallet, 
  Coins, 
  ArrowLeft,
  User,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import Swal from 'sweetalert2';

// نمایش پیام
const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: { popup: 'font-vazir' },
  });
};

// تبدیل اعداد فارسی به انگلیسی
const persianToEnglishDigits = (str) => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  let result = str.toString();
  for (let i = 0; i < persianDigits.length; i++) {
    result = result.replace(new RegExp(persianDigits[i], 'g'), englishDigits[i]);
  }
  return result;
};

// فرمت نهایی (برای نمایش در Swal و موجودی کاربر)
const formatNumber = (value, isGold = false) => {
  if (!value || isNaN(parseFloat(value))) return '';
  const number = parseFloat(value);
  if (isNaN(number)) return '';

  if (isGold) {
    const trimmed = number.toFixed(3).replace(/\.?0+$/, '');
    return trimmed;
  } else {
    return Math.floor(number).toLocaleString('fa-IR');
  }
};

// فرمت برای نمایش در اینپوت (بدون حذف نقطه در حین تایپ)
const formatInputDisplay = (raw, isGold) => {
  if (!raw) return '';
  if (isGold) {
    return raw; // طلایی: همون مقدار خام
  } else {
    const num = parseInt(raw, 10);
    return isNaN(num) ? '' : num.toLocaleString('fa-IR');
  }
};

export default function AdminChargeAccount() {
  const [search, setSearch] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chargeType, setChargeType] = useState('rial');
  const [amount, setAmount] = useState(''); // مقدار خام (مثل "1.234" یا "1234567")
  const [description, setDescription] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // ریسپانسیو
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // رفرش توکن
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  // جستجوی کاربر
  const searchUser = async () => {
    if (!search.trim()) {
      showAlert('لطفاً شناسه، نام یا شماره کاربر را وارد کنید', 'warning');
      return;
    }

    setLoading(true);
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      const res = await api.get(`/api/adminCharge/user/${search}`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });

      setUser(res.data);
      showAlert('کاربر یافت شد', 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'کاربر یافت نشد';
      showAlert(msg, 'error');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // تغییر مقدار (کنترل کامل)
  const handleAmountChange = (e) => {
    let input = persianToEnglishDigits(e.target.value);
    let raw = input.replace(/[^\d.]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) raw = parts[0] + '.' + parts.slice(1).join('');
  
    if (chargeType === 'rial') {
      raw = raw.replace(/\./g, '');
      setAmount(raw);
    } else {
      raw = parts[0] + (parts[1] !== undefined ? '.' + parts[1] : '');
      setAmount(raw);
    }
  };

  // شارژ حساب
  const handleCharge = async () => {
    if (!user) {
      showAlert('ابتدا کاربر را جستجو کنید', 'warning');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      showAlert('مبلغ باید بیشتر از صفر باشد', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: 'آیا مطمئن هستید؟',
      html: `
        <div class="text-right">
          <p class="text-lg font-bold mb-2">${formatNumber(amount, chargeType === 'gold')} ${chargeType === 'rial' ? 'ریال' : 'گرم طلا'}</p>
          <p class="text-sm text-gray-600">به حساب <strong>${user.first_name} ${user.last_name}</strong> اضافه شود؟</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'بله، شارژ کن',
      cancelButtonText: 'خیر',
      customClass: { popup: 'font-vazir' },
    });

    if (!result.isConfirmed) return;

    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      await api.post('/api/adminCharge/charge', {
        userId: user.id,
        type: chargeType,
        amount: parseFloat(amount),
        description,
      }, {
        headers: { Authorization: `Bearer ${newToken}` },
      });

      showAlert('شارژ با موفقیت انجام شد', 'success');

      // بروزرسانی اطلاعات کاربر
      const res = await api.get(`/api/adminCharge/user/${user.id}`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      setUser(res.data);
      setAmount('');
      setDescription('');
    } catch (err) {
      showAlert(err.response?.data?.message || 'خطا در شارژ', 'error');
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
    }
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-4xl mx-auto">
        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-600 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
         برگشت
        </motion.button>

        {/* هدر */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <Wallet className="w-9 h-9" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">شارژ دستی حساب کاربر</h2>
          <p className="text-gray-600 mt-2">افزایش موجودی ریالی یا طلایی کاربران</p>
        </motion.div>

        {/* جستجو */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/30"
        >
          <div className="flex gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchUser()}
              placeholder="شناسه، نام یا شماره کاربر..."
              className="flex-1 px-5 py-4 bg-gray-50/80 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={searchUser}
              disabled={loading}
              className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-bold text-white shadow-lg transition-all duration-300 ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700'
              }`}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              {loading ? 'در حال جستجو...' : 'جستجو'}
            </motion.button>
          </div>
        </motion.div>

        {/* اطلاعات کاربر */}
        <AnimatePresence>
          {user && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 mt-6 border border-white/30"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {user.first_name?.[0] || 'ک'}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{user.first_name} {user.last_name}</h3>
                    <p className="text-gray-600 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {user.phone_number}
                    </p>
                    <p className="text-sm text-gray-500">شناسه: {user.id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full md:w-auto">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-8 h-8 text-blue-600" />
                      <div className="text-right">
                        <p className="text-xs text-gray-600">موجودی ریالی</p>
                        <p className="text-lg font-bold text-blue-700">{formatNumber(user.balance_rial)} ریال</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4 border border-yellow-100">
                    <div className="flex items-center gap-3">
                      <Coins className="w-8 h-8 text-yellow-600" />
                      <div className="text-right">
                        <p className="text-xs text-gray-600">موجودی طلایی</p>
                        <p className="text-lg font-bold text-yellow-700">{formatNumber(user.balance_gold, true)} گرم</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* نوع شارژ */}
              <div className="flex gap-4 mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setChargeType('rial');
                    setAmount('');
                  }}
                  className={`flex-1 py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center gap-3 ${
                    chargeType === 'rial'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Wallet className="w-6 h-6" />
                  شارژ ریالی
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setChargeType('gold');
                    setAmount('');
                  }}
                  className={`flex-1 py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center gap-3 ${
                    chargeType === 'gold'
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Coins className="w-6 h-6" />
                  شارژ طلایی
                </motion.button>
              </div>

              {/* مقدار */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {chargeType === 'rial' ? 'مبلغ (ریال)' : 'مقدار (گرم طلا)'}
                </label>
                <input
  type="text"
  value={formatInputDisplay(amount, chargeType === 'gold')}
  onChange={handleAmountChange}
  onBlur={() => {
    if (chargeType === 'gold' && amount) {
      const parts = amount.split('.');
      if (parts[1]) parts[1] = parts[1].slice(0, 3);
      const trimmed = parts[0] + (parts[1] ? '.' + parts[1] : '');
      setAmount(trimmed);
    }
  }}
  placeholder={chargeType === 'rial' ? 'مثلاً ۱٬۲۳۴٬۵۶۷' : 'مثلاً ۱٫۲۳۴'}
  className="w-full px-5 py-4 bg-gray-50/80 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 text-left font-mono"
  dir="ltr"
  inputMode={chargeType === 'rial' ? 'numeric' : 'decimal'}
/>
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {chargeType === 'rial' ? 'فقط اعداد صحیح' : 'حداکثر ۳ رقم اعشار'}
                </p>
              </div>

              {/* توضیحات */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">توضیحات (اختیاری)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="مثلاً: شارژ دستی توسط ادمین"
                  rows="3"
                  className="w-full px-5 py-4 bg-gray-50/80 border border-gray-14 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 resize-none"
                />
              </div>

              {/* دکمه شارژ */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCharge}
                className="w-full py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3"
              >
                <CheckCircle className="w-6 h-6" />
                ثبت شارژ
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* فوتر */}
        <div className="text-center mt-10 text-xs text-gray-500">
          <p>© ۱۴۰۴ - ESF.Gold | پنل مدیریت</p>
        </div>
      </div>

      <style jsx>{`
        .font-vazir { font-family: 'Vazir', sans-serif !important; }
      `}</style>
    </div>
  );
}